/**
 * 手势。这是"原生感"里最容易做砸、也最难查的一块。
 *
 * 三条规则，每条都对应一类真机上才会出现的失败：
 *
 * 1. **长按类交互的三件套缺一不可**：
 *    - `e.preventDefault()` —— iOS 上不做就是"按住就选中文字 + 弹放大镜"；
 *    - `setPointerCapture(e.pointerId)` —— 手指滑出按钮后还能收到 pointermove，
 *      不做的话"上滑取消"永远不生效；
 *    - CSS `touch-action: none` —— 不做的话浏览器会把这次拖拽拿去滚动页面。
 *
 *    ⚠️ 但这两件事都有副作用：`preventDefault()` 会抑制兼容 click，
 *    `setPointerCapture()` 会把兼容 click 重定向到捕获元素。**两者都会让这个元素里的
 *    按钮、链接失灵**，而视觉上完全正常。判据见下面 `shouldIgnoreGesture` 的注释。
 *
 * 2. **一个手势只能有一个语义**。图片放大后横向拖是"平移"，未放大时横向拖是"翻页"。
 *    不区分的话，用户放大看细节时会莫名其妙翻到下一张。判据见 useSwipe 的注释。
 *
 * 3. **滑动手势只绑在该响应的容器上**。绑在页面上就会和滚动、浏览器下拉刷新打架，
 *    玩两下就把页面刷新了。
 */
import { useCallback, useEffect, useRef, } from 'react';
/** 超过这个位移才捕获指针——在这之前保持"可能只是一次点击"的状态 */
const CAPTURE_SLOP = 4;
/** 手指长按之后这段时间内的 contextmenu 视为同一次交互，不重复弹菜单 */
const DUP_CONTEXTMENU_MS = 800;
/** 手势不该接管的落点：落在这些元素上时，让它们自己处理 */
const INTERACTIVE_SELECTOR = 'button, a[href], input, select, textarea, [role="button"], [role="link"], [contenteditable="true"]';
/**
 * 这一次按下该不该被手势接管。
 *
 * ⚠️ **判据是"捕获元素里还有没有别的可交互子元素"，不是"这是不是可交互元素"。**
 *
 * - 手势装在按钮自己身上（长按一个按钮）→ `closest()` 返回的就是 currentTarget，
 *   这是主用法，必须接管；
 * - 手势装在一个列表行上，而行里有个"更多"按钮 → 按在那个按钮上时必须**放手**，
 *   否则 `preventDefault()` + `setPointerCapture()` 会让它彻底点不动。
 *
 * 这条规则对所有会 preventDefault 或捕获指针的手势都成立
 * （已在 Sheet 与 ImageViewer 上各踩过一次：抽屉里的按钮、看图器的关闭按钮全失灵）。
 */
export function shouldIgnoreGesture(target, currentTarget) {
    if (!currentTarget || !target?.closest)
        return false;
    const hit = target.closest(INTERACTIVE_SELECTOR);
    if (!hit)
        return false;
    // 落点就是手势宿主本身 → 这是主用法，接管
    if (hit === currentTarget)
        return false;
    // 落在宿主内部的另一个可交互元素上 → 让它自己处理
    return currentTarget.contains(hit);
}
export function useLongPress(options) {
    const { delayMs = 500, moveTolerance = 10 } = options;
    const timer = useRef(null);
    const origin = useRef(null);
    const fired = useRef(false);
    const captured = useRef(false);
    /** 上一次真的弹出菜单的时刻，用来给 contextmenu 判重 */
    const firedAt = useRef(-1e9);
    const opts = useRef(options);
    opts.current = options;
    const clear = useCallback(() => {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = null;
        origin.current = null;
    }, []);
    useEffect(() => clear, [clear]);
    return {
        onPointerDown(e) {
            // 只认主按键/手指；右键要留给桌面的原生菜单
            if (e.button !== 0)
                return;
            // 落在内部的按钮/链接上：整个手势都不开始，让那个控件自己处理
            if (shouldIgnoreGesture(e.target, e.currentTarget))
                return;
            // iOS：不 preventDefault 就是按住选中文字 + 放大镜
            e.preventDefault();
            // ⚠️ 这里**不** setPointerCapture。捕获推迟到确认真的在移动之后（见 onPointerMove）：
            // 在按下的那一刻捕获，会把兼容 click 重定向到捕获元素。
            captured.current = false;
            fired.current = false;
            origin.current = { x: e.clientX, y: e.clientY };
            const target = e.currentTarget;
            timer.current = setTimeout(() => {
                fired.current = true;
                firedAt.current = performance.now();
                opts.current.onStart?.();
                opts.current.onLongPress({ x: origin.current?.x ?? 0, y: origin.current?.y ?? 0, target });
            }, delayMs);
        },
        onPointerMove(e) {
            if (!origin.current)
                return;
            const dx = e.clientX - origin.current.x;
            const dy = e.clientY - origin.current.y;
            const moved = Math.hypot(dx, dy);
            // 一旦真的动起来就捕获：这时候已经不可能是点击了，而"手指滑出元素后还能收到
            // pointermove"正是「上滑取消」赖以生效的东西。触屏本来就有隐式捕获，
            // 这一句主要是给鼠标/触控笔补上。
            if (!captured.current && moved > CAPTURE_SLOP) {
                e.currentTarget.setPointerCapture?.(e.pointerId);
                captured.current = true;
            }
            if (moved > moveTolerance) {
                clear();
                opts.current.onCancel?.();
            }
        },
        onPointerUp(e) {
            if (captured.current)
                e.currentTarget.releasePointerCapture?.(e.pointerId);
            captured.current = false;
            const wasPending = timer.current !== null;
            clear();
            // 没到时长就抬手 = 普通点击。这条不做，长按元素就点不动了。
            // （`preventDefault()` 已经把兼容 click 抑制掉了，所以点击必须由我们自己合成。）
            if (wasPending && !fired.current)
                opts.current.onClick?.();
        },
        onPointerCancel(e) {
            if (captured.current)
                e.currentTarget.releasePointerCapture?.(e.pointerId);
            captured.current = false;
            clear();
            opts.current.onCancel?.();
        },
        /**
         * 挡掉系统菜单，并把桌面的**右键**转成同一个长按语义。
         *
         * ⚠️ 只挡不转是桌面上的一个真实缺陷：鼠标用户没有"长按"这个动作，
         * 而右键又被吃掉了——那个上下文菜单他**根本打不开**。
         * 手机上系统长按菜单会盖住自己的菜单，必须挡；桌面上挡完要把它接回来。
         */
        onContextMenu(e) {
            e.preventDefault();
            // 手指长按在部分浏览器上也会派发 contextmenu。那一下的菜单已经由定时器弹过了，
            // 用时间戳判重而不是 fired 标志——fired 会一直挂到下一次 pointerdown 才清，
            // 中间这段时间里真正的右键会被误判成重复。
            if (performance.now() - firedAt.current < DUP_CONTEXTMENU_MS)
                return;
            clear();
            const x = typeof e.clientX === 'number' ? e.clientX : 0;
            const y = typeof e.clientY === 'number' ? e.clientY : 0;
            if (x === 0 && y === 0)
                return;
            firedAt.current = performance.now();
            opts.current.onStart?.();
            opts.current.onLongPress({ x, y, target: e.currentTarget });
        },
        style: { touchAction: 'none' },
    };
}
/**
 * ⚠️ 用它的容器上要加 `touch-action: none`，并且**只绑在该响应的容器上**。
 * 绑在页面级元素上会和滚动、下拉刷新打架。
 */
/** 判定"这是滑动而不是点击"的位移阈值（px） */
const SWIPE_SLOP = 6;
export function useSwipe(options) {
    const { threshold = 60, axisRatio = 1.5 } = options;
    const start = useRef(null);
    const opts = useRef(options);
    opts.current = options;
    return {
        onPointerDown(e) {
            if (e.button !== 0)
                return;
            if (opts.current.enabled && !opts.current.enabled()) {
                start.current = null;
                return;
            }
            // 落在内部的按钮/链接上：不接管（同 useLongPress 的判据）
            if (shouldIgnoreGesture(e.target, e.currentTarget)) {
                start.current = null;
                return;
            }
            // ⚠️ **不在这里 setPointerCapture。** 捕获会把兼容 click 重定向到捕获元素，
            // 于是这个容器里的按钮、链接全部点不动——而视觉上完全正常。
            // 推迟到确认真的在滑动之后再捕获（见下面的 onPointerMove）。
            start.current = { x: e.clientX, y: e.clientY, active: true, captured: false };
        },
        onPointerMove(e) {
            const s = start.current;
            if (!s?.active)
                return;
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            if (!s.captured) {
                if (Math.hypot(dx, dy) <= SWIPE_SLOP)
                    return;
                // 确认是滑动了才捕获：这样手指滑出容器也还能收到 move，而轻点不受影响
                e.currentTarget.setPointerCapture?.(e.pointerId);
                s.captured = true;
            }
            opts.current.onMove?.({ dx, dy });
        },
        onPointerUp(e) {
            const s = start.current;
            if (s?.captured)
                e.currentTarget.releasePointerCapture?.(e.pointerId);
            start.current = null;
            if (!s?.active)
                return;
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            const ax = Math.abs(dx);
            const ay = Math.abs(dy);
            opts.current.onEnd?.();
            // 主轴够长 **且** 明显压过副轴才算数
            if (ax >= threshold && ax > ay * axisRatio)
                opts.current.onSwipe(dx > 0 ? 'right' : 'left', { dx, dy });
            else if (ay >= threshold && ay > ax * axisRatio)
                opts.current.onSwipe(dy > 0 ? 'down' : 'up', { dx, dy });
        },
        onPointerCancel(e) {
            e.currentTarget.releasePointerCapture?.(e.pointerId);
            start.current = null;
            opts.current.onEnd?.();
        },
    };
}
/** 轻震动反馈。不支持的平台静默跳过——它是锦上添花，不能因为它报错。 */
export function haptic(pattern = 8) {
    try {
        navigator.vibrate?.(pattern);
    }
    catch {
        /* iOS Safari 没有 vibrate，这很正常 */
    }
}
//# sourceMappingURL=gestures.js.map